import { createApiClient, createPublicApiClient } from './client'
import { ApiResponse } from './types'

export interface ShareLinkPermissions {
  viewItinerary: boolean
  viewCampsiteCoordinates: boolean
  viewCrew: boolean
  viewAnnouncements: boolean
  viewRadioChannel: boolean
  viewMeetupLocation: boolean
}

export interface CreateShareLinkDto {
  permissions: ShareLinkPermissions
  /** When true, viewers can join the trip as guest or member from this link. */
  allow_join?: boolean
  expires_at?: string
}

export interface ShareLinkResponse {
  id: string
  trip_id: string
  token: string
  permissions: ShareLinkPermissions
  allow_join: boolean
  expires_at: string | null
  view_count: number
  last_accessed_at: string | null
  created_at: string
}

export interface ValidateShareLinkResponse {
  valid: boolean
  tripId?: string
  permissions?: ShareLinkPermissions
  expiresAt?: string | null
  allowJoin?: boolean
}

export class ShareLinksApi {
  private apiClient
  private publicApiClient

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
    this.publicApiClient = createPublicApiClient()
  }

  async createShareLink(tripId: string, data: CreateShareLinkDto): Promise<ShareLinkResponse> {
    const response = await this.apiClient.post<ApiResponse<ShareLinkResponse>>(
      `/trips/share-links/trips/${tripId}`,
      data
    )
    return response.data
  }

  async getShareLinks(tripId: string): Promise<ShareLinkResponse[]> {
    const response = await this.apiClient.get<ApiResponse<ShareLinkResponse[]>>(
      `/trips/share-links/trips/${tripId}`
    )
    return response.data
  }

  async validateShareLink(token: string): Promise<ValidateShareLinkResponse> {
    const response = await this.publicApiClient.get<ApiResponse<ValidateShareLinkResponse>>(
      `/trips/share-links/validate/${token}`
    )
    return response.data
  }

  async getTripViaShareLink(token: string): Promise<any> {
    const response = await this.publicApiClient.get<ApiResponse<any>>(
      `/trips/share-links/trip/${token}`
    )
    return response.data
  }

  async revokeShareLink(linkId: string): Promise<void> {
    await this.apiClient.delete(`/trips/share-links/${linkId}`)
  }

  async updateShareLink(linkId: string, data: Partial<CreateShareLinkDto>): Promise<ShareLinkResponse> {
    const response = await this.apiClient.patch<ApiResponse<ShareLinkResponse>>(
      `/trips/share-links/${linkId}`,
      data
    )
    return response.data
  }

  async joinTripWithShareToken(
    token: string,
    data: {
      join_type: 'guest' | 'member'
      guest_name?: string
      guest_email?: string
      guest_session_id?: string
    }
  ): Promise<{
    success: boolean
    message: string
    trip_id?: string
    guest_session_id?: string
    guest_access_token?: string
    is_existing_guest?: boolean
  }> {
    // Use authenticated client so token is sent when joining as member
    const response = await this.apiClient.post<ApiResponse<{
      success: boolean
      message: string
      trip_id?: string
      guest_session_id?: string
      guest_access_token?: string
      is_existing_guest?: boolean
    }>>(`/trips/share-links/join`, { token, ...data })
    return response.data
  }
}

// Permission preset helpers
export const permissionPresets = {
  fullAccess: {
    viewItinerary: true,
    viewCampsiteCoordinates: true,
    viewCrew: true,
    viewAnnouncements: true,
    viewRadioChannel: true,
    viewMeetupLocation: true
  } as ShareLinkPermissions,
  basicInfo: {
    viewItinerary: true,
    viewCampsiteCoordinates: false,
    viewCrew: true,
    viewAnnouncements: true,
    viewRadioChannel: true,
    viewMeetupLocation: true
  } as ShareLinkPermissions,
  minimal: {
    viewItinerary: false,
    viewCampsiteCoordinates: false,
    viewCrew: false,
    viewAnnouncements: false,
    viewRadioChannel: false,
    viewMeetupLocation: false
  } as ShareLinkPermissions
}
